// Snapshot agent tab — a conversation with the Claude Code pipeline that lives
// in the separate agentContainer project.
//
// This is a SESSION wrapper, not a one-shot runner. Each card is one
// conversation: you send a message, the agent works and answers, and the next
// message continues from there — the container resumes the same Claude session
// in the same git worktree, so the agent's earlier files and context are still
// there. The work reaches master only when you press "End session".
//
// A session outlives the browser. Closing the tab, or even restarting the
// dashboard, does not end it: the container owns the worktree and the
// transcript, so the picker at the top lists conversations you can pick back up
// and attaching replays the history.
//
// LAYOUT RULE: this tab has ONE scroll region — the tab body — with exactly one
// deliberate exception. Nested scrollers made the earlier version of this panel
// impossible to read (four of them, three deep), so nothing sets its own
// max-height; long content is handled by collapsing instead.
//
// The exception is the agent activity log, and it earns it: it is an append-only
// terminal that can run to hundreds of rows in a single turn, and left to grow
// it buries the answer it was working towards under its own scrollback. So it
// gets a bounded height and its own scroller, and follows its own tail. Nothing
// else may — if a second one of these appears, the rule has been lost.
//
// The composer sits ABOVE the transcript, and the transcript runs newest-first:
// the two things you reach for — the box you type in and the turn that just
// answered — are both at the top, so neither walks away from you as a session
// grows.
//
// Visibility rules (mirrored on the server, which is where they are enforced):
//   * anyone who can work on the base can run a session and import snapshots
//   * only admins see the step-by-step agent log, cost, and turn stats

import WorkspaceSetupProgress from "./workspaceSetupProgress";
import { missingSession, recoveryLabel } from "../modules/snapshotAgentRecovery";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  BAR_BG,
  CONTROL_BG,
  CONTROL_BORDER,
  FIELD_LABEL,
  PANEL_BG,
  SECTION_DIVIDER
} from "./snapshotEditor/panelPalette";
import AgentMarkdown from "./snapshotEditor/agentMarkdown";
import {
  connectSnapshotAgentSession,
  cleanupSnapshotAgentAttachments,
  fetchSnapshotAgentFlows,
  fetchSnapshotAgentSessions,
  newSessionId,
  removeSnapshotAgentAttachment,
  nameSnapshotAgentSessionFromPrompt,
  renameSnapshotAgentSession,
  uploadSnapshotAgentAttachment
} from "../modules/snapshotAgentSocket";
import {
  SNAPSHOT_DRAG_TYPE,
  appendToPrompt,
  draggedSnapshot,
  setDraggedSnapshot,
  attachmentTokenEndAtCaret,
  attachmentTokenRanges,
  deleteAttachmentTokenAtCaret,
  expandAttachmentTokenSelection,
  removedAttachmentReferences,
} from "../modules/snapshotAgentPromptTokens";
import { snapshotFlowOf } from "../modules/snapshotEditorApi";
import { currentStage, formatStalled, backgroundSummary } from "../modules/snapshotAgentStage";
import { ConvertConfirmDialog, ConvertSessionFields, convertPrompt } from "./snapshotEditor/convertSession";

import { agentEffortOptions, agentModelOptions, sessionCost } from "../modules/snapshotAgentModels";

// Semantic, not surface: these survive the palette because they carry meaning.
const STATUS_COLORS = {
  running: "#ffb74d",
  queued: "#b39ddb",
  idle: "#a5d6a7",
  ended: "#8f8f8f",
  expired: "#8f8f8f",
  failed: "#ef9a9a"
};

const MAX_AGENT_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const SESSION_DISCOVERY_MS = 10 * 1000;
// Sessions these automations start open as cards without anyone clicking.
const AUTOMATION_ACTORS = new Set(["asana-webhook", "jira-webhook"]);
const CONVERT_FLOW = "playableGenerator";

// The prompt composer paints its text in one layer and keeps the native
// textarea transparent above it for selection and the caret. These metrics
// must be identical on both layers: MUI's InputBase otherwise contributes its
// default 0.00938em letter-spacing only to the textarea, which accumulates into
// a visible caret offset on long lines.
const PROMPT_TEXT_METRICS = {
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontSize: 13,
  fontStyle: "normal",
  fontWeight: 400,
  fontVariant: "normal",
  letterSpacing: "normal",
  wordSpacing: "normal",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  overflowWrap: "break-word",
  wordBreak: "normal",
  textTransform: "none",
  textIndent: 0,
};

function attachmentType(file) {
  const mime = String(file.type || "").toLowerCase();
  const extension = String(file.name || "").split(".").pop().toLowerCase();
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(extension)) return "Image";
  if (mime.startsWith("video/") || ["mp4", "webm", "mov", "m4v", "avi"].includes(extension)) return "Video";
  if (mime.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(extension)) return "Audio";
  return "File";
}

function visibleAgentPrompt(prompt) {
  return String(prompt || "").split("\n\n[Attached files for agent]\n")[0];
}

const ATTACHMENT_TOKEN_COLORS = {
  Image: { color: "#90caf9", background: "rgba(33, 150, 243, 0.18)" },
  Video: { color: "#ce93d8", background: "rgba(156, 39, 176, 0.18)" },
  Audio: { color: "#ffcc80", background: "rgba(255, 152, 0, 0.18)" },
  File: { color: "#80cbc4", background: "rgba(0, 150, 136, 0.18)" },
};

function highlightedPromptNodes(value) {
  const text = String(value || "");
  const nodes = [];
  const tokenPattern = /\[(Image|Video|Audio|File)#\d+\]/g;
  let previousEnd = 0;
  for (const match of text.matchAll(tokenPattern)) {
    if (match.index > previousEnd) nodes.push(text.slice(previousEnd, match.index));
    const kind = match[1];
    nodes.push(
      <span
        key={`${match.index}-${match[0]}`}
        style={{
          ...ATTACHMENT_TOKEN_COLORS[kind],
          borderRadius: 3,
          // This layer sits exactly underneath a transparent textarea. Any
          // padding, bold weight or inline-block sizing would make the painted
          // token wider than the textarea text and make its caret appear inside
          // the token even when selectionEnd is correctly at the end.
          fontWeight: "inherit",
        }}
      >
        {match[0]}
      </span>
    );
    previousEnd = match.index + match[0].length;
  }
  if (previousEnd < text.length) nodes.push(text.slice(previousEnd));
  return nodes;
}

const useStyles = makeStyles(() => ({
  // The tab body — the ONE scroll region. See the layout rule at the top.
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 16px",
    height: "100%",
    overflowY: "auto",
    backgroundColor: PANEL_BG,
    color: "#ddd"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px"
  },
  stage: {
    fontSize: "11px",
    color: FIELD_LABEL,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "240px"
  },
  stageStalled: {
    fontSize: "11px",
    color: "#e6c07b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "280px"
  },
  eyebrow: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: FIELD_LABEL
  },
  outlinedButton: {
    color: FIELD_LABEL,
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    textTransform: "none"
  },

  // ── Session picker ──────────────────────────────────────────────────────
  picker: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px"
  },
  pickerCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    padding: "6px 10px",
    borderRadius: "4px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    textTransform: "none",
    color: "#ddd",
    minWidth: "160px"
  },
  pickerWhen: { fontSize: "12.5px", fontWeight: 600, lineHeight: 1.3 },
  pickerLines: { display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 },
  pickerMeta: { fontSize: "11.5px", color: FIELD_LABEL, lineHeight: 1.3 },
  pickerOwner: { fontSize: "12px", fontWeight: 600, color: "#cfd4da" },

  // ── Agent queue ─────────────────────────────────────────────────────────
  queueList: { display: "flex", flexDirection: "column", gap: "4px", marginTop: "6px" },
  queueRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#ddd" },
  queuePosition: { fontWeight: 600, minWidth: "24px", color: STATUS_COLORS.queued },

  // ── Session card ────────────────────────────────────────────────────────
  card: {
    border: `1px solid ${SECTION_DIVIDER}`,
    borderRadius: "6px",
    backgroundColor: BAR_BG,
    overflow: "hidden"
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "8px 10px",
    borderBottom: `1px solid ${SECTION_DIVIDER}`
  },
  headerTop: { display: "flex", alignItems: "center", gap: "8px" },
  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0
  },
  statusLabel: { fontSize: "12.5px", fontWeight: 600, textTransform: "capitalize", flexShrink: 0 },
  // The one thing in this row that may give up space, and the first thing
  // worth reading in it: everything beside it keeps its size, so the name
  // gets whatever is left rather than being crushed to "OBW…".
  // A line of its own, so it is never cut: a long name wraps rather than
  // ending in an ellipsis.
  titleText: { fontSize: "13px", fontWeight: 600, color: "#e2e5ea", cursor: "text", lineHeight: 1.35, overflowWrap: "anywhere" },
  titleEmpty: { fontSize: "12px", color: FIELD_LABEL, cursor: "text", opacity: 0.7 },
  startNameInput: { fontSize: "13px", color: "#fff", backgroundColor: "transparent", border: "none", borderBottom: "1px solid #555", padding: "4px 0", width: "100%", outline: "none" },
  titleInput: { fontSize: "12.5px", fontWeight: 600, color: "#e2e5ea", backgroundColor: "#262626", border: "1px solid #4a4a4a", borderRadius: "3px", padding: "1px 6px", width: "220px", outline: "none" },
  // One line, never two. It gives up space after the name does, and is cut
  // with an ellipsis rather than pushing the actions off the row.
  meta: { fontSize: "11.5px", color: FIELD_LABEL, whiteSpace: "nowrap", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" },
  // The session id is never worth a line of its own — it is only ever needed
  // when a conversation has to be found in the container's logs, runs folder
  // or worktrees. One glyph on the row, the id itself on hover.
  sessionIdIcon: { fontSize: "14px", color: FIELD_LABEL, flexShrink: 0, cursor: "help", "&:hover": { color: "#ddd" } },
  sessionIdTip: { fontFamily: "monospace", fontSize: "11.5px", letterSpacing: "0.2px" },
  headerActions: { marginLeft: "auto", flexShrink: 0, display: "flex", alignItems: "center", gap: "8px" },
  // Who started it, read at a glance: the list is scanned for a person as
  // often as for a name, and 11px grey loses that race.
  owner: { fontSize: "12.5px", fontWeight: 600, color: "#cfd4da", whiteSpace: "nowrap", flexShrink: 0 },
  // The transcript sits BELOW the composer, so the seam between the two is
  // drawn here — `body` only renders once there are turns, which keeps the
  // card from ending on a stray rule while a session is still empty.
  body: { padding: "10px", borderTop: `1px solid ${SECTION_DIVIDER}` },

  // Expand/collapse-all — deliberately quiet: two small glyphs above the
  // transcript, not a pair of labelled buttons competing with the turns.
  turnTools: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginBottom: "4px"
  },
  contextNote: { fontSize: "11.5px", color: FIELD_LABEL },
  // A link, not a button: it sits inside a sentence and pressing it is a
  // suggestion the sentence just made.
  compactLink: {
    color: FIELD_LABEL,
    textDecoration: "underline",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { color: "#ddd" }
  },
  compactArmed: { color: "#ffb74d", textDecoration: "underline", cursor: "pointer", userSelect: "none" },
  // Quiet until you reach for it: destructive, but it sits beside the button
  // people actually press, so it must not compete for attention.
  deleteButton: {
    padding: "4px",
    fontSize: "17px",
    color: FIELD_LABEL,
    "&:hover": { color: "#ef5350" }
  },
  turnToolButton: {
    padding: "3px",
    color: FIELD_LABEL,
    "&:hover": { color: "#ddd" }
  },

  // ── One turn ────────────────────────────────────────────────────────────
  turn: {
    borderRadius: "4px",
    border: `1px solid ${SECTION_DIVIDER}`,
    marginBottom: "8px",
    overflow: "hidden"
  },
  turnHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "5px 8px",
    cursor: "pointer",
    backgroundColor: CONTROL_BG,
    userSelect: "none"
  },
  turnIndex: { fontSize: "11.5px", color: FIELD_LABEL, fontWeight: 600 },
  // The prompt's author, next to the turn's own numbers. Quieter than the
  // session header's name, because it only matters when it differs from it.
  promptBy: {
    fontSize: "11.5px",
    color: "#8f8f8f",
    maxWidth: "140px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  turnBody: { padding: "8px" },
  caret: {
    padding: "2px",
    color: FIELD_LABEL,
    transition: "transform 120ms ease"
  },
  caretOpen: { transform: "rotate(180deg)" },

  // Not a message bubble: this turn is a marker in the conversation, not
  // something anyone said.
  commandNote: {
    fontSize: "12.5px",
    fontStyle: "italic",
    color: FIELD_LABEL,
    borderLeft: `2px solid ${CONTROL_BORDER}`,
    padding: "4px 0 4px 10px"
  },

  userMessage: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "13px",
    lineHeight: 1.6,
    backgroundColor: "#33404d",
    borderRadius: "4px",
    padding: "8px 10px"
  },

  // Disclosure for the agent's step log — the thing that used to scroll.
  disclosure: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "3px 6px",
    borderRadius: "4px",
    cursor: "pointer",
    color: FIELD_LABEL,
    fontSize: "11.5px",
    userSelect: "none",
    "&:hover": { backgroundColor: CONTROL_BG }
  },
  // The one bounded scroller in this tab — see the layout rule at the top.
  log: {
    backgroundColor: "#151619",
    border: `1px solid ${SECTION_DIVIDER}`,
    borderRadius: "4px",
    padding: "8px",
    marginTop: "4px",
    fontFamily: "monospace",
    fontSize: "11.5px",
    lineHeight: 1.55,
    // Tall enough for a dozen rows, short enough to leave the answer on screen
    // beneath it. `resize: vertical` lets anyone reading a long run drag it
    // taller without us having to guess a second number.
    maxHeight: "260px",
    overflowY: "auto",
    overflowX: "hidden",
    resize: "vertical",
    // A terminal's own gutter, so rows do not sit under the scrollbar.
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { width: "9px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#3f434b",
      borderRadius: "4px"
    },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" }
  },
  // NOT a two-column grid. A fixed left gutter costs its width on every line a
  // row wraps to, so a long message reflowed into a column half the pane wide
  // while the gutter sat empty beside it. The kind is an inline prefix instead:
  // short rows still line up on `minWidth`, and a wrapped one returns to the
  // left edge and uses the whole pane. Neither layout costs a second line.
  // The "nothing has come back yet" note. Deliberately quiet at first — a
  // session legitimately spends its first minutes building a workspace — and
  // amber once that stops being a normal wait.
  silentNote: {
    margin: "6px 0 2px",
    padding: "8px 10px",
    borderLeft: "2px solid #4c5361",
    backgroundColor: "#23262c",
    borderRadius: "3px",
    fontSize: "12px",
    color: "#b9bec7"
  },
  silentNoteLate: { borderLeftColor: "#ffb74d", color: "#e8c48a" },
  silentNoteHint: { marginTop: "3px", fontSize: "11px", color: "#8b909a" },

  logRow: { padding: "2px 0" },
  logKind: {
    display: "inline-block",
    minWidth: "62px",
    marginRight: "8px",
    color: "#7e9cd8",
    verticalAlign: "top"
  },
  logText: { whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#cfcfcf" },
  // The tail marker: says the log is live rather than simply ended here.
  logCursor: {
    display: "inline-block",
    width: "7px",
    height: "13px",
    backgroundColor: "#7e9cd8",
    verticalAlign: "text-bottom",
    animation: "$blink 1.1s step-end infinite"
  },
  "@keyframes blink": { "50%": { opacity: 0 } },

  // Only the spacing: type, colour and wrapping belong to AgentMarkdown, which
  // has to set them per block anyway (a table cell is not a paragraph).
  answer: { marginTop: "8px" },
  snapshotChip: {
    height: "20px",
    fontSize: "11px",
    backgroundColor: CONTROL_BG,
    color: FIELD_LABEL,
    border: `1px solid ${CONTROL_BORDER}`,
    marginRight: "4px",
    marginTop: "6px"
  },

  // ── Composer ────────────────────────────────────────────────────────────
  composer: {
    padding: "10px"
  },
  // Wraps so a narrow panel pushes the button to the next line instead of off the card.
  composerRow: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginTop: "8px" },
  muted: { color: "#8f8f8f", fontSize: "12px" },
  error: { color: "#ef9a9a", fontSize: "12px" }
}));

/**
 * Put the agent's answer on the clipboard as the MARKDOWN it was written in.
 *
 * Selecting the rendered answer by hand copies what the browser painted — a
 * table comes back as a run of loose words, and the headings lose their `##`.
 * The source is still in `turn.resultText`, so copy that instead: what lands in
 * the paste buffer is exactly what the agent wrote.
 *
 * navigator.clipboard needs a secure context. Production is https and dev is
 * localhost, so it is there in practice; the textarea path is the cheap
 * insurance for anything served over plain http.
 */
async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const scratch = document.createElement("textarea");
  scratch.value = text;
  // Off-screen rather than hidden: a display:none textarea cannot be selected.
  scratch.style.position = "fixed";
  scratch.style.top = "-1000px";
  scratch.setAttribute("readonly", "");
  document.body.appendChild(scratch);
  try {
    scratch.select();
    document.execCommand("copy");
  } finally {
    document.body.removeChild(scratch);
  }
}

function formatDuration(startedAt, finishedAt) {
  if (!startedAt) return "";
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const seconds = Math.max(0, Math.round((end - new Date(startedAt)) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function formatTimestamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${date.toLocaleDateString()} ${time}`;
}

function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return today ? time : `${date.toLocaleDateString()} ${time}`;
}

/**
 * Record a card's live session id on its slot (see markSessionOpen). Returns
 * the SAME array when the slot already has it: every session update calls
 * this, and a new array would re-render every card for one card's step.
 */
export function slotsWithSessionId(slots, key, sessionId) {
  if (!slots.some((slot) => slot.key === key && slot.sessionId !== sessionId)) return slots;
  return slots.map((slot) =>
    slot.key === key && slot.sessionId !== sessionId ? { ...slot, sessionId } : slot
  );
}

/** Sessions with an open card must not also be offered in the picker. */
export function pickableAgentSessions(sessions, slots) {
  const openIds = new Set((slots || [])
    .map((slot) => slot.sessionId || _.get(slot, "initialSession.sessionId"))
    .filter(Boolean));
  return (sessions || []).filter((session) => !openIds.has(session.sessionId));
}

/**
 * How much of this conversation is paid for in full on the next turn.
 *
 * Taken from the LAST turn that reported usage, not summed across the session:
 * every turn resends the whole conversation, so the newest turn's figure IS the
 * current context size. Summing would count the same context ten times over.
 *
 * `input + cacheWrite` and not cacheRead: cached tokens are served at a fraction
 * of the price, so they are not what makes a long session expensive. This number
 * is what compaction actually reduces.
 */
export function uncachedTokens(session) {
  const turns = _.get(session, "turns") || [];
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const current = turns[index].contextUncachedTokens;
    if (current != null) return current;
  }
  return 0;
}

/**
 * A turn nobody wrote prose for: a slash command, shown as what it DID.
 *
 * The prompt stays `/compact` on the server — that is what was actually sent,
 * and a replayed turn and a live one must agree. Only the presentation changes,
 * because "/compact" in a message bubble reads like something the user is
 * saying to the agent, when it is really a mark in the conversation: everything
 * above it was summarised, and that is the thing worth being able to see.
 */
function commandNote(prompt) {
  const trimmed = (prompt || "").trim();
  if (trimmed === "/compact") {
    return "Previous turns were compacted — the agent summarised them to free up context.";
  }
  return trimmed.startsWith("/") ? `Ran ${trimmed}` : null;
}

/** 12800 → "12.8K". Below a thousand the exact number is more use than a unit. */
export function formatTokens(count) {
  if (count < 1000) return String(count);
  return `${(count / 1000).toFixed(1)}K`;
}

/** Total spend across a conversation — each turn reports its own cost. */

// ───────────────────────────────────────────────────────────────────────────
// One turn: the message, the (collapsible) step log, the answer.
// ───────────────────────────────────────────────────────────────────────────
function Turn({ turn, index, isAdmin, isOpen, onToggle, isLogOpen, onToggleLog, uncertain = false, queue = null }) {
  const classes = useStyles();
  const isRunning = turn.status === "running" && !uncertain;
  // Waiting for an agent slot in the container: nothing runs yet, so no stage and no silence alarm.
  const isQueued = isRunning && Boolean(queue);
  const stepCount = (turn.steps || []).length;
  const cost = _.get(turn, "stats.totalCostUsd");
  const userPrompt = visibleAgentPrompt(turn.prompt);
  const [copied, setCopied] = useState(false);

  // A turn the agent container has not said ONE WORD for yet.
  //
  // `firstEventAt`, not `steps`: the step log is admin-only, so a non-admin saw
  // a spinner and nothing else — and the two states behind that spinner are
  // completely different. "Working" is normal; "nothing has come back at all"
  // is the shape of the failure that used to sit here for hours (the container
  // could no longer start `claude`, and the card said Running the whole time).
  const isSilent = isRunning && !isQueued && !turn.firstEventAt;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!isSilent) return undefined;
    // Every other re-render of this card is driven by an arriving step. The
    // whole point of this state is that none are arriving, so the clock has to
    // move on its own or it reads as frozen — which is exactly what it is.
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [isSilent]);
  const silentSeconds = isSilent
    ? Math.max(0, Math.round((Date.now() - new Date(turn.startedAt)) / 1000))
    : 0;

  const stage = isRunning && !isQueued ? currentStage(turn) : null;
  // Why a turn whose agent has gone quiet is still running: it waits on these.
  const background = isRunning && !isQueued ? backgroundSummary(turn.backgroundTasks) : null;
  useEffect(() => {
    if (!isRunning || isSilent) return undefined;
    const timer = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(timer);
  }, [isRunning, isSilent]);

  // The tick is the only confirmation — a snackbar for something this small
  // would cover the answer you just copied.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(timer);
  }, [copied]);

  // Follow the tail like a terminal — but only from the tail.
  //
  // This scrolls the log ELEMENT, never scrollIntoView: that walks up every
  // scrollable ancestor and would drag the whole tab, which is the bug the
  // transcript's old auto-scroll had. And it holds still whenever the reader
  // has scrolled up, because yanking someone off the line they are reading is
  // worse than making them press End.
  const logRef = useRef(null);
  useEffect(() => {
    const el = logRef.current;
    if (!el || !isLogOpen) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 60) el.scrollTop = el.scrollHeight;
  }, [stepCount, isLogOpen]);

  const copyAnswer = async (event) => {
    // The header toggles the turn; the button must not close it under the click.
    event.stopPropagation();
    try {
      await copyText(turn.resultText || "");
      setCopied(true);
    } catch (err) {
      console.error("[snapshotAgent] could not copy the answer:", err);
    }
  };

  return (
    <div className={classes.turn}>
      <div className={classes.turnHeader} onClick={onToggle}>
        <IconButton size="small" className={`${classes.caret} ${isOpen ? classes.caretOpen : ""}`}>
          <ExpandMoreIcon fontSize="inherit" />
        </IconButton>
        <span className={classes.turnIndex}>Turn {index + 1}</span>
        {isRunning ? <CircularProgress size={12} /> : null}
        <div style={{ flex: 1 }} />
        {turn.resultText ? (
          <Tooltip title={copied ? "Copied" : "Copy the answer as markdown"}>
            <IconButton size="small" className={classes.turnToolButton} onClick={copyAnswer}>
              {copied ? (
                <CheckIcon style={{ fontSize: "14px", color: "#a5d6a7" }} />
              ) : (
                <FileCopyOutlinedIcon style={{ fontSize: "13px" }} />
              )}
            </IconButton>
          </Tooltip>
        ) : null}
        {/* Who wrote this prompt. Absent on a replayed turn — the container's
            transcript has no dashboard account on it — and absent is correct
            there: the alternative is stamping the session's starter onto
            someone else's prompt. */}
        {isQueued ? (
          <span className={classes.stage}>
            Waiting in queue · #{queue.position}
          </span>
        ) : null}
        {stage ? (
          <Tooltip title={stage.detail || ""}>
            {/* A quiet agent with background work is waiting, not stuck: no stall warning then. */}
            <span className={stage.isStalled && !background ? classes.stageStalled : classes.stage}>
              {stage.label}
              {stage.isStalled && !background ? ` · no progress for ${formatStalled(stage.stalledMs)}` : ""}
            </span>
          </Tooltip>
        ) : null}
        {background ? (
          <Tooltip
            title={background.descriptions.length ? (
              <div style={{ whiteSpace: "pre-line" }}>{background.descriptions.join("\n")}</div>
            ) : ""}
          >
            <span className={classes.stage}>{background.label}</span>
          </Tooltip>
        ) : null}
        {turn.promptBy ? <span className={classes.promptBy}>{turn.promptBy}</span> : null}
        <span className={classes.meta}>
          {turn.startedAt ? `${formatTimestamp(turn.startedAt)} · ` : ""}
          {uncertain && ["running", "unknown"].includes(turn.status) ? "Awaiting current status" : formatDuration(turn.startedAt, turn.finishedAt)}
          {isAdmin && cost ? ` · $${Number(cost).toFixed(3)}` : ""}
          {turn.error ? " · failed" : ""}
        </span>
      </div>

      {/* unmountOnExit: a closed turn renders NOTHING. Without it every turn's
          markdown — and its step log — was re-parsed on every session update,
          and a session update arrives with every step the agent takes. */}
      <Collapse in={isOpen} unmountOnExit>
        <div className={classes.turnBody}>
          {commandNote(userPrompt) ? (
            <div className={classes.commandNote}>{commandNote(userPrompt)}</div>
          ) : (
            <div className={classes.userMessage}>{userPrompt}</div>
          )}

          <WorkspaceSetupProgress setup={turn.setup} finishedAt={turn.finishedAt} isRunning={isRunning} uncertain={uncertain}
            classes={classes} formatDuration={formatDuration} />

          {isSilent ? (
            <div className={`${classes.silentNote} ${silentSeconds > 120 ? classes.silentNoteLate : ""}`}>
              {silentSeconds > 120
                ? `Nothing has come back from the agent for ${formatDuration(turn.startedAt, null)}.`
                : `Starting the agent — no output yet (${formatDuration(turn.startedAt, null)}).`}
              <div className={classes.silentNoteHint}>
                {silentSeconds > 120
                  ? "Waiting for workspace status. Setup is cancelled if it does not finish within 20 minutes."
                  : "Opening a session prepares a workspace first; the agent's own log starts after that."}
              </div>
            </div>
          ) : null}

          {isAdmin && stepCount ? (
            <>
              <div className={classes.disclosure} onClick={onToggleLog}>
                <ExpandMoreIcon
                  fontSize="inherit"
                  style={{ transform: isLogOpen ? "rotate(180deg)" : "none", transition: "transform 120ms ease" }}
                />
                Agent activity · {stepCount} step{stepCount === 1 ? "" : "s"}
                {turn.droppedSteps ? ` (${turn.droppedSteps} earlier trimmed)` : ""}
              </div>
              <Collapse in={isLogOpen} unmountOnExit>
                <div className={classes.log} ref={logRef}>
                  {(turn.steps || []).map((step, stepIndex) => (
                    <div className={classes.logRow} key={`${step.at}-${stepIndex}`}>
                      <span
                        className={classes.logKind}
                        style={step.isError ? { color: "#ef9a9a" } : undefined}
                      >
                        {step.kind === "tool" ? step.name || "tool" : step.kind}
                      </span>
                      <span className={classes.logText}>{step.summary}</span>
                    </div>
                  ))}
                  {isRunning ? <span className={classes.logCursor} /> : null}
                </div>
              </Collapse>
            </>
          ) : null}

          {turn.resultText ? (
            <AgentMarkdown text={turn.resultText} className={classes.answer} />
          ) : null}
          {turn.error ? <Typography className={classes.error} style={{ marginTop: "6px" }}>{turn.error}</Typography> : null}

          {(turn.newSnapshots || []).length ? (
            <div>
              {turn.newSnapshots.map((name) => (
                <Chip key={name} size="small" label={name} className={classes.snapshotChip} />
              ))}
            </div>
          ) : null}
        </div>
      </Collapse>
    </div>
  );
}

Turn.propTypes = {
  turn: PropTypes.object,
  index: PropTypes.number,
  isAdmin: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  isLogOpen: PropTypes.bool,
  onToggleLog: PropTypes.func,
  uncertain: PropTypes.bool,
  /** The agent queue place while this turn waits for a slot; null otherwise. */
  queue: PropTypes.object
};

// ───────────────────────────────────────────────────────────────────────────
// One conversation: its own transcript, composer and WebSocket.
// ───────────────────────────────────────────────────────────────────────────
/**
 * A session's name, in its card header.
 *
 * A base can hold a dozen conversations and "4 turns · asya" tells them apart
 * only by accident. Click the name to change it; an empty name clears it and
 * the header goes back to what it showed before. The name lives in the
 * container's session meta, so it is the same name for everyone.
 */
/**
 * A name for a session nobody named: the snapshot it produced.
 *
 * That is what the session is FOR, and it is what someone scanning the list is
 * looking for. Two sessions can make snapshots of the same name — a second
 * attempt at the same brief — so a name already in use gets a number, as a
 * file manager would. `taken` is every other session's name.
 */
export function titleFromSnapshot(fileName, taken) {
  const stem = String(fileName || "").trim().replace(/\.json$/i, "").trim();
  if (!stem) return "";

  const used = new Set((taken || []).filter(Boolean));
  if (!used.has(stem)) return stem;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidate = `${stem} ${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return stem;
}

function SessionTitle({ baseId, session, onRenamed }) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(session.title || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setDraft(session.title || ""), [session.sessionId, session.title]);

  const save = async () => {
    const next = draft.trim();
    setIsEditing(false);
    if (next === (session.title || "")) return;
    setIsSaving(true);
    try {
      const answer = await renameSnapshotAgentSession(baseId, session.sessionId, next);
      if (onRenamed) onRenamed(answer.title || null);
    } catch (_err) {
      // The name is a convenience; the card keeps working without it.
      setDraft(session.title || "");
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <input
        className={classes.titleInput}
        autoFocus
        value={draft}
        maxLength={80}
        placeholder="Name this session"
        disabled={isSaving}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") save();
          if (event.key === "Escape") {
            setDraft(session.title || "");
            setIsEditing(false);
          }
        }}
        onClick={(event) => event.stopPropagation()}
      />
    );
  }

  return (
    <span
      className={session.title ? classes.titleText : classes.titleEmpty}
      title="Rename this session"
      onClick={(event) => {
        event.stopPropagation();
        setIsEditing(true);
      }}
    >
      {session.title || "Name this session"}
    </span>
  );
}

function SessionCard({
  baseId,
  onSessionRenamed,
  takenTitles,
  isActive,
  isAdmin,
  available,
  baseFlow,
  flows,
  models,
  initialSession,
  isConvert,
  observedOperationId,
  onSessionOpened,
  onTurnFinished,
  onSessionClosed,
  onRemove,
  canRemove,
  createSnackbar
}) {
  const classes = useStyles();
  const [draft, setDraft] = useState("");
  const [model, setModel] = useState(_.get(initialSession, "model") || "");
  const [effort, setEffort] = useState(_.get(initialSession, "reasoningEffort") || "");
  // Typed before the first prompt; applied once the session exists to be named.
  const [startTitle, setStartTitle] = useState("");
  // Read inside the socket callback, which must not re-subscribe when it changes.
  const startTitleRef = useRef("");
  // Asked for once per session: the model either names it or declines, and
  // either way it is not asked again.
  const namedFromPromptRef = useRef(false);
  const takenTitlesRef = useRef([]);
  useEffect(() => {
    takenTitlesRef.current = takenTitles || [];
  }, [takenTitles]);
  useEffect(() => {
    startTitleRef.current = startTitle.trim();
  }, [startTitle]);
  const [session, setSession] = useState(initialSession || null);
  // A resumed session reports the flow it was STARTED with, which can differ
  // from the base's current mapping (a renamed flow, say); showing the session's
  // own value keeps the label honest about where its work will land.
  const usedFlow = _.get(session, "flow") || _.get(initialSession, "flow") || baseFlow || "";
  const usedFlowDescription = _.get(
    (flows || []).find((f) => f.name === usedFlow),
    "description",
    ""
  );
  const defaultModel = _.get((flows || []).find((f) => f.name === usedFlow), "defaultModel");
  const modelOptions = agentModelOptions(models, defaultModel, model);
  const selectedModel = (models || []).find((m) => m.id === (model || defaultModel));
  const effortOptions = agentEffortOptions(selectedModel, effort);
  const supportsEffort = _.get(selectedModel, "efforts.length", 0) > 0;
  const supportsCompact = _.get(session, "capabilities.supportsCompact", _.get(selectedModel, "capabilities.supportsCompact", true));
  const [isSending, setIsSending] = useState(false);
  const [convert, setConvert] = useState({ game: null, branch: "", detail: null });
  const [isConfirmingConvert, setIsConfirmingConvert] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  // The delete confirmation. A dialog rather than an armed button: this is
  // irreversible and it sits beside the button that ships the work to master,
  // so it should cost a deliberate answer, not a second click in the same spot.
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  // First press arms "compact", the second sends it. Inline and reversible, so
  // it earns the lighter confirmation that deleting a session does not.
  const [armedCompact, setArmedCompact] = useState(false);
  // Attaching found no such conversation in the container — see the null branch
  // in handleSessionUpdate.
  const [isMissing, setIsMissing] = useState(false);
  // The socket's own health, as connectSnapshotAgentSession reports it:
  // "online", "reconnecting" while it backs off, "offline" once it has given
  // up. A dropped socket used to be a red snackbar and a card that silently
  // swallowed everything typed into it afterwards; the session it views is
  // untouched by any of that, so this belongs in the header, not in an alert.
  const [connectionState, setConnectionState] = useState(null);
  const [connectionDetail, setConnectionDetail] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isDropTarget, setIsDropTarget] = useState(false);

  // Explicit open/closed per turn, keyed by index. Undefined = use the default
  // (turns open; their step log open only while the turn is running).
  const [turnOverrides, setTurnOverrides] = useState({});
  const [logOverrides, setLogOverrides] = useState({});

  const connectionRef = useRef(null);
  const settledTurnsRef = useRef(0);
  const sessionIdRef = useRef(_.get(initialSession, "sessionId") || null);
  const attachmentInputRef = useRef(null);
  const promptComposerRef = useRef(null);
  const promptHighlightRef = useRef(null);
  const promptHighlightContentRef = useRef(null);
  const promptInputRef = useRef(null);
  const nextAttachmentIdRef = useRef(0);
  const attachmentCountersRef = useRef(null);
  if (attachmentCountersRef.current === null) {
    const counts = {};
    for (const turn of _.get(initialSession, "turns", [])) {
      const matches = String(turn.prompt || "").matchAll(/\[(Image|Video|Audio|File)#(\d+)\]/g);
      for (const match of matches) counts[match[1]] = Math.max(counts[match[1]] || 0, Number(match[2]));
    }
    attachmentCountersRef.current = counts;
  }

  const ensureSessionId = useCallback(() => {
    if (!sessionIdRef.current) sessionIdRef.current = newSessionId();
    return sessionIdRef.current;
  }, []);

  const uploadAttachment = useCallback(async (attachment) => {
    if (attachment.file.size > MAX_AGENT_ATTACHMENT_BYTES) {
      setAttachments((current) => current.map((item) => item.id === attachment.id
        ? { ...item, status: "error", error: "Files must be 10 MB or smaller." }
        : item));
      return;
    }

    setAttachments((current) => current.map((item) => item.id === attachment.id
      ? { ...item, status: "uploading", error: null }
      : item));
    try {
      const uploaded = await uploadSnapshotAgentAttachment(baseId, ensureSessionId(), attachment.file);
      setAttachments((current) => current.map((item) => item.id === attachment.id
        ? { ...item, status: "uploaded", relativePath: uploaded.relativePath, error: null }
        : item));
      setDraft((current) => current.includes(attachment.reference)
        ? current
        : `${current}${current && !/\s$/.test(current) ? " " : ""}${attachment.reference}`);
    } catch (err) {
      setAttachments((current) => current.map((item) => item.id === attachment.id
        ? { ...item, status: "error", error: err.message || "Upload failed." }
        : item));
    }
  }, [baseId, ensureSessionId]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    const queued = files.map((file) => {
      const type = attachmentType(file);
      const count = (attachmentCountersRef.current[type] || 0) + 1;
      attachmentCountersRef.current[type] = count;
      return {
        id: ++nextAttachmentIdRef.current,
        file,
        type,
        reference: `[${type}#${count}]`,
        status: "queued",
        error: null,
      };
    });
    setAttachments((current) => [...current, ...queued]);

    for (const attachment of queued) {
      // Sequential uploads bound browser memory and make each failed item easy
      // to identify/retry even when many files are dropped together.
      // eslint-disable-next-line no-await-in-loop
      await uploadAttachment(attachment);
    }
  }, [uploadAttachment]);

  const removeAttachment = useCallback(async (attachment, { draftAlreadyUpdated = false } = {}) => {
    if (attachment.status === "deleting") return false;
    if (attachment.relativePath && sessionIdRef.current) {
      setAttachments((current) => current.map((item) => item.id === attachment.id
        ? { ...item, status: "deleting", error: null }
        : item));
      try {
        await removeSnapshotAgentAttachment(baseId, sessionIdRef.current, attachment.relativePath);
      } catch (err) {
        setAttachments((current) => current.map((item) => item.id === attachment.id
          ? { ...item, status: "deleteError", error: err.message || "File could not be removed." }
          : item));
        // A keyboard edit may already have removed the token. Restore it when
        // disk cleanup fails so the prompt never silently loses its file link.
        if (draftAlreadyUpdated) {
          setDraft((current) => current.includes(attachment.reference)
            ? current
            : `${current}${current && !/\s$/.test(current) ? " " : ""}${attachment.reference}`);
        }
        return false;
      }
    }
    setAttachments((current) => current.filter((item) => item.id !== attachment.id));
    if (!draftAlreadyUpdated) {
      setDraft((current) => current.replace(attachment.reference, "").replace(/\s{2,}/g, " ").trimStart());
    }
    return true;
  }, [baseId]);

  const handlePromptChange = useCallback((event) => {
    const nextDraft = event.target.value;
    const removedReferences = new Set(removedAttachmentReferences(draft, nextDraft));
    const removedAttachments = attachments.filter((attachment) =>
      attachment.relativePath &&
      attachment.status !== "deleting" &&
      removedReferences.has(attachment.reference)
    );
    setDraft(nextDraft);
    void (async () => {
      for (const attachment of removedAttachments) {
        // Sequential removal lets the final request reliably prune the now
        // empty session and tmp directories.
        // eslint-disable-next-line no-await-in-loop
        await removeAttachment(attachment, { draftAlreadyUpdated: true });
      }
    })();
  }, [attachments, draft, removeAttachment]);

  const status = _.get(session, "status") || null;
  const isRunning = !isMissing && status === "running";
  // The container holds this turn until an agent slot frees up, in arrival order.
  const queue = isRunning ? session?.queue || null : null;
  const hasActiveOperation = !isMissing && ["running", "unreachable"].includes(status);
  // "failed" is closed too: a session whose end could not be merged no longer
  // exists in the container — its work was committed to a branch and the room
  // was torn down — so neither another message nor another End can reach it.
  const isClosed =
    status === "ended" || status === "expired" || status === "failed" || isMissing;
  const hasStarted = Boolean(session);
  const turns = useMemo(() => _.get(session, "turns") || [], [session]);

  const closeConnection = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
    setConnectionState(null);
  }, []);

  const cleanupAttachments = useCallback(async (sessionId) => {
    if (!sessionId) return true;
    try {
      await cleanupSnapshotAgentAttachments(baseId, sessionId);
      return true;
    } catch (err) {
      console.error("[snapshotAgent] attachment cleanup failed:", err.message);
      if (createSnackbar) createSnackbar(`Temporary files could not be cleaned up: ${err.message}`, "error");
      return false;
    }
  }, [baseId, createSnackbar]);

  const handleSessionUpdate = useCallback(
    (nextSession, closed) => {
      // Attach came back empty: the container has no such conversation — its
      // worktree was cleaned up, or it was ended from somewhere else. It cannot
      // be continued or ended, so the card says so and the picker lets it go
      // rather than offering it again on the next ✕.
      if (!nextSession) {
        setIsMissing(true);
        setSession((current) => missingSession(current));
        setIsSending(false);
        setIsEnding(false);
        closeConnection();
        const goneId = _.get(initialSession, "sessionId");
        void cleanupAttachments(goneId || sessionIdRef.current);
        if (goneId && typeof onSessionClosed === "function") onSessionClosed(goneId);
        return;
      }

      setIsMissing(false);
      sessionIdRef.current = nextSession.sessionId || sessionIdRef.current;
      // A session that already carries a name is never renamed behind the
      // user's back, whoever gave it that name.
      if (nextSession.title) namedFromPromptRef.current = true;
      setSession(nextSession);
      if (nextSession.sessionId && typeof onSessionOpened === "function") {
        onSessionOpened(nextSession.sessionId);
      }
      // The name typed before START, applied the moment there is a session to
      // put it on. Once only: after that the header owns the name.
      if (nextSession.sessionId && startTitleRef.current) {
        const wanted = startTitleRef.current;
        startTitleRef.current = "";
        renameSnapshotAgentSession(baseId, nextSession.sessionId, wanted)
          .then((answer) => {
            setSession((current) => (current ? { ...current, title: answer.title || null } : current));
            if (onSessionRenamed) onSessionRenamed(nextSession.sessionId, answer.title || null);
          })
          .catch(() => undefined);
      }
      // Still unnamed: the instruction that opened it says what it is for, so
      // a small model reads that and names it — what a chat app does with its
      // first message. As soon as the prompt exists, not when the turn ends:
      // the name is what the card is scanned by while the agent works. Once
      // per session; the model may decline, and then the snapshot rule below
      // gets its turn.
      const opening = _.get(nextSession, ["turns", 0, "prompt"], "");
      if (!nextSession.title && !namedFromPromptRef.current && nextSession.sessionId && opening) {
        namedFromPromptRef.current = true;
        nameSnapshotAgentSessionFromPrompt(baseId, nextSession.sessionId, opening)
          .then((answer) => {
            if (!answer || !answer.title) return;
            setSession((current) => (current ? { ...current, title: answer.title } : current));
            if (onSessionRenamed) onSessionRenamed(nextSession.sessionId, answer.title);
          })
          .catch(() => undefined);
      }

      if (nextSession.model) setModel(nextSession.model);
      if (nextSession.reasoningEffort) setEffort(nextSession.reasoningEffort);
      setIsSending(false);

      // Announce each turn as it settles: the snapshot tree needs to reload and
      // the user wants to know what was produced.
      const nextTurns = nextSession.turns || [];
      const settled = nextTurns.filter((turn) => turn.status && turn.status !== "running").length;
      if (settled > settledTurnsRef.current) {
        settledTurnsRef.current = settled;
        const lastTurn = nextTurns[settled - 1];
        if (typeof onTurnFinished === "function") onTurnFinished(nextSession, lastTurn);

        // Still unnamed, and the turn produced a snapshot: that snapshot is
        // what the session is, so it is what the session is called.
        const produced = (lastTurn && lastTurn.newSnapshots) || [];
        if (!nextSession.title && !namedFromPromptRef.current && produced.length && nextSession.sessionId) {
          const derived = titleFromSnapshot(produced[0], takenTitlesRef.current);
          if (derived) {
            renameSnapshotAgentSession(baseId, nextSession.sessionId, derived)
              .then((answer) => {
                setSession((current) => (current ? { ...current, title: answer.title || null } : current));
                if (onSessionRenamed) onSessionRenamed(nextSession.sessionId, answer.title || null);
              })
              .catch(() => undefined);
          }
        }
      }

      if (closed) {
        setIsEnding(false);
        closeConnection();
        void cleanupAttachments(nextSession.sessionId);
        // Ended, expired, or failed to merge — all three are the end of this
        // conversation. Say so upwards while the card is still open, because by
        // the time the ✕ is pressed the card is gone and nobody is left to tell
        // the picker that the session it still lists is finished.
        if (typeof onSessionClosed === "function") onSessionClosed(nextSession.sessionId);
      }
    },
    [cleanupAttachments, closeConnection, initialSession, onSessionClosed, onSessionOpened, onTurnFinished]
  );

  const openConnection = useCallback(
    (options) => {
      closeConnection();
      connectionRef.current = connectSnapshotAgentSession({
        baseId,
        onSession: handleSessionUpdate,
        onStatus: (state, detail) => { setConnectionState(state); setConnectionDetail(detail); },
        onError: (err) => {
          setIsSending(false);
          setIsEnding(false);
          console.error("[applovinAgent] session error:", err.message);
          if (createSnackbar) createSnackbar(err.message, "error");
        },
        ...options
      });
      return connectionRef.current;
    },
    [baseId, closeConnection, handleSessionUpdate, createSnackbar]
  );

  // Attach to an existing conversation on mount (re-open the tab, or pick one
  // started days ago — the container replays its transcript).
  useEffect(() => {
    if (!initialSession || !baseId) return undefined;
    if (["ended", "expired", "failed"].includes(initialSession.status)) return undefined;
    settledTurnsRef.current = (initialSession.turns || []).filter(
      (turn) => turn.status && turn.status !== "running"
    ).length;
    openConnection({ action: "attach", sessionId: initialSession.sessionId });
    return () => closeConnection();
    // Attach exactly once for this card's initial session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastObservedOperationId = useRef(initialSession?.operationId || null);
  useEffect(() => {
    if (!observedOperationId || !initialSession?.sessionId) return;
    if (lastObservedOperationId.current === observedOperationId) return;
    lastObservedOperationId.current = observedOperationId;
    if (isSending || session?.status === "running") return;
    // A turn created by Asana has no browser socket of its own. Reattaching
    // makes the existing card replay it and follow its operation stream.
    const connection = connectionRef.current;
    if (connection) connection.reconnect();
  }, [initialSession, isSending, observedOperationId, session?.status]);

  // A staged upload belongs to this unsent card, not to a resumable session.
  // If the user reloads or leaves the page, remove its temporary files too.
  useEffect(() => {
    const onPageHide = (event) => {
      if (event.persisted || hasStarted || !sessionIdRef.current) return;
      cleanupSnapshotAgentAttachments(baseId, sessionIdRef.current, { keepalive: true })
        .catch((err) => console.warn("[snapshotAgent] page-exit attachment cleanup failed:", err.message));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [baseId, hasStarted]);

  // No auto-scroll. The transcript runs newest-first, so a live turn and its
  // steps grow at the TOP of the card, where the reader already is — there is
  // nothing to follow. The old sentinel-plus-scrollIntoView did follow, and
  // every arriving step yanked the tab (scrollIntoView moves every scrollable
  // ancestor, not just the tab body) toward the OLDEST turn.

  const submit = useCallback((promptOverride) => {
    const trimmed = (typeof promptOverride === "string" ? promptOverride : draft).trim();
    if (!trimmed || (!hasStarted && !(selectedModel?.available || selectedModel?.retryable))) return;
    if (attachments.some((attachment) => attachment.status !== "uploaded")) return;

    const includedAttachments = attachments
      .filter((attachment) => trimmed.includes(attachment.reference))
      .map(({ reference, relativePath }) => ({ reference, relativePath }));

    setIsSending(true);
    setDraft("");
    setAttachments([]);

    if (!hasStarted) {
      const sessionId = ensureSessionId();
      if (typeof onSessionOpened === "function") onSessionOpened(sessionId);
      openConnection({
        action: "start",
        sessionId,
        prompt: trimmed,
        attachments: includedAttachments,
        // No flow here on purpose: the server takes it from the base document.
        // Sending one would be ignored, and pretending otherwise invites the
        // belief that a session can be pointed at another pipeline from here.
        model: model || undefined,
        reasoningEffort: supportsEffort && effort ? effort : undefined
      });
      return;
    }

    // The socket may have been dropped while the session sat idle; reattaching
    // before sending keeps "come back tomorrow and keep talking" working.
    const connection =
      connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.send(trimmed, includedAttachments);
  }, [draft, attachments, hasStarted, openConnection, ensureSessionId, model, effort, supportsEffort, session,
    selectedModel, onSessionOpened]);

  const startNow = useCallback(() => {
    if (!session || !queue) return;
    const connection = connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.startNow();
  }, [session, queue, openConnection]);
  const stopTurn = useCallback(() => {
    if (!session || isMissing) return;
    const connection = connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.stop();
  }, [session, isMissing, openConnection]);
  const retryTurn = useCallback(() => {
    const last = turns[turns.length - 1];
    if (!last || !session) return;
    const connection = connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    setIsSending(true);
    connection.send(last.agentStarted
      ? "The previous execution failed. Review the conversation and workspace, verify any uncertain external actions, and continue the unfinished request without repeating completed work."
      : last.prompt);
  }, [session, turns, openConnection]);

  /**
   * Ask for the socket back after it gave up backing off.
   *
   * The connection reopens itself for every ordinary drop; this is only for the
   * case it decided was hopeless — a token that expired while the tab sat open,
   * an API that was down for longer than the budget. Nothing about the session
   * itself is affected: it is a view being reopened, not work being restarted.
   */
  const reconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.reconnect();
      return;
    }
    if (session) openConnection({ action: "attach", sessionId: session.sessionId });
  }, [session, openConnection]);

  const endSession = useCallback(() => {
    if (!session) return;
    setIsEnding(true);
    const connection =
      connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.end();
  }, [session, openConnection]);

  /**
   * Delete the session outright — no merge, and nothing kept to come back to.
   *
   * Two clicks, not one: this is irreversible and it sits next to the button
   * that ships the work to master, so the second click is what says the first
   * was not a slip. It disarms itself after a few seconds, so the button cannot
   * stay loaded behind a tab switch.
   */
  const discardSession = useCallback(() => {
    if (!session) return;
    setIsDeleteOpen(false);
    setIsEnding(true);
    const connection =
      connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.discard();
  }, [session, openConnection]);

  const closeCard = useCallback(async () => {
    if (!hasStarted && sessionIdRef.current) {
      const cleaned = await cleanupAttachments(sessionIdRef.current);
      if (!cleaned) return;
    }
    closeConnection();
    // A newly started session only exists in this card's local state until the
    // next full list fetch. Hand it to the parent before unmounting so it can be
    // continued immediately without a browser reload.
    if (typeof onRemove === "function") onRemove(session);
  }, [cleanupAttachments, closeConnection, hasStarted, onRemove, session]);

  const handlePromptKeyDown = useCallback((event) => {
    event.stopPropagation();
    const input = event.currentTarget;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const key = event.key;

    if (key === "Backspace" || key === "Delete") {
      const direction = key === "Backspace" ? "backward" : "forward";
      const deletion = deleteAttachmentTokenAtCaret(draft, start, end, direction);
      if (deletion) {
        event.preventDefault();
        const removedReferences = new Set(removedAttachmentReferences(draft, deletion.value));
        const removedAttachments = attachments.filter((attachment) =>
          removedReferences.has(attachment.reference)
        );
        setDraft(deletion.value);
        void (async () => {
          for (const attachment of removedAttachments) {
            // eslint-disable-next-line no-await-in-loop
            await removeAttachment(attachment, { draftAlreadyUpdated: true });
          }
          requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(deletion.caret, deletion.caret);
          });
        })();
        return;
      }
    }

    const ranges = attachmentTokenRanges(draft);
    const cutShortcut = (event.metaKey || event.ctrlKey) && key.toLowerCase() === "x";
    if (start !== end && ((!event.metaKey && !event.ctrlKey && !event.altKey) || cutShortcut)) {
      const expanded = expandAttachmentTokenSelection(draft, start, end);
      if (expanded.start !== start || expanded.end !== end) {
        input.setSelectionRange(expanded.start, expanded.end);
      }
      return;
    }

    if (start === end) {
      const token = ranges.find((range) => start > range.start && start < range.end);
      const tokenAtEdge = ranges.find((range) =>
        (key === "ArrowLeft" && range.end === start) || (key === "ArrowRight" && range.start === start)
      );
      const protectedToken = token || tokenAtEdge;
      if (!protectedToken) return;
      if (key === "ArrowLeft") {
        event.preventDefault();
        input.setSelectionRange(protectedToken.start, protectedToken.start);
      } else if (key === "ArrowRight") {
        event.preventDefault();
        input.setSelectionRange(protectedToken.end, protectedToken.end);
      } else if (!event.metaKey && !event.ctrlKey && !event.altKey && (key.length === 1 || key === "Enter")) {
        // Keep the reference intact; place typed text after the token.
        input.setSelectionRange(protectedToken.end, protectedToken.end);
      }
    }
  }, [attachments, draft, removeAttachment]);

  const handlePromptPaste = useCallback((event) => {
    const input = event.currentTarget;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start === end) return;
    const expanded = expandAttachmentTokenSelection(draft, start, end);
    if (expanded.start !== start || expanded.end !== end) {
      input.setSelectionRange(expanded.start, expanded.end);
    }
  }, [draft]);

  const protectPromptCaret = useCallback((event) => {
    const input = event.currentTarget;
    if (input.selectionStart !== input.selectionEnd) return;
    const tokenEnd = attachmentTokenEndAtCaret(draft, input.selectionStart);
    if (tokenEnd !== null) input.setSelectionRange(tokenEnd, tokenEnd);
  }, [draft]);

  /**
   * The textarea becomes scrollable at maxRows. Its vertical scrollbar then
   * takes width away from the text, while an independently sized highlight
   * layer would keep wrapping at the old width. Anchor the painted layer to
   * the textarea's measured client box so both use the exact same line width.
   */
  const syncPromptHighlightGeometry = useCallback(() => {
    const composer = promptComposerRef.current;
    const highlight = promptHighlightRef.current;
    const input = promptInputRef.current;
    if (!composer || !highlight || !input) return;

    const composerRect = composer.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();
    highlight.style.left = `${inputRect.left - composerRect.left}px`;
    highlight.style.top = `${inputRect.top - composerRect.top}px`;
    highlight.style.width = `${input.clientWidth}px`;
    highlight.style.height = `${input.clientHeight}px`;

    if (promptHighlightContentRef.current) {
      promptHighlightContentRef.current.style.transform =
        `translate(${-input.scrollLeft}px, ${-input.scrollTop}px)`;
    }
  }, []);

  useLayoutEffect(() => {
    syncPromptHighlightGeometry();
    const input = promptInputRef.current;
    const composer = promptComposerRef.current;
    if (!input || !composer) return undefined;

    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(syncPromptHighlightGeometry)
      : null;
    observer?.observe(input);
    observer?.observe(composer);
    window.addEventListener("resize", syncPromptHighlightGeometry);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncPromptHighlightGeometry);
    };
  }, [isActive, isClosed, syncPromptHighlightGeometry]);

  // TextareaAutosize may change its own height/overflow in a layout effect
  // after a paste. A frame-late measurement sees that final geometry.
  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(syncPromptHighlightGeometry);
    return () => window.cancelAnimationFrame(frame);
  }, [draft, syncPromptHighlightGeometry]);

  /**
   * Ask the CLI to compact its own context.
   *
   * Sent as an ordinary turn whose prompt is `/compact`: the container pipes the
   * prompt straight into `claude`, which handles slash commands itself, so this
   * needs nothing on the server. It IS a real turn — it takes minutes and it
   * costs, because compaction reads the whole conversation to summarise it.
   */
  const compactSession = useCallback(() => {
    if (!supportsCompact) return;
    if (!armedCompact) {
      setArmedCompact(true);
      return;
    }
    setArmedCompact(false);
    setIsSending(true);
    const connection =
      connectionRef.current || openConnection({ action: "attach", sessionId: session.sessionId });
    connection.send("/compact");
  }, [armedCompact, openConnection, session, supportsCompact]);

  // Disarm on its own, so the link cannot sit loaded behind a tab switch.
  useEffect(() => {
    if (!armedCompact) return undefined;
    const timer = setTimeout(() => setArmedCompact(false), 4000);
    return () => clearTimeout(timer);
  }, [armedCompact]);

  const isTurnOpen = (index) => turnOverrides[index] !== undefined ? turnOverrides[index] : false;
  // Expand/collapse all: an explicit override for every turn, so later turns
  // arriving after the click still follow the default (closed) rather than
  // inheriting a decision that was made about a different transcript.
  const setAllTurns = (open) =>
    setTurnOverrides(turns.reduce((acc, turn, index) => ({ ...acc, [index]: open }), {}));
  const isLogOpen = (index, turn) =>
    logOverrides[index] !== undefined ? logOverrides[index] : turn.status === "running";

  const busy = hasActiveOperation || isSending;
  const isConvertStart = Boolean(isConvert) && !hasStarted;
  const transportUncertain = connectionState && connectionState !== "online" || status === "unreachable" || session?.transport;
  const recovery = session?.transport || turns[turns.length - 1]?.recovery;
  const cost = sessionCost(session);

  // `canRemove` is false for the last card left, which is what used to leave an
  // ended session with no ✕ at all. A card holding a conversation always gets
  // one — the parent hands back a fresh empty card when the last is closed, so
  // the tab is never left with nothing.
  const canClose = canRemove || hasStarted;

  /**
   * Hidden tab: keep the card, draw none of it.
   *
   * Everything above this line is state and a live WebSocket, and all of it has
   * to survive a look at another tab — the session this card is attached to, the
   * turns it has opened, and above all the prompt half-typed into the composer.
   * The tab used to be unmounted instead, which is where that draft went.
   *
   * What it must NOT keep doing while hidden is the expensive half. Every step
   * the agent takes arrives as a message and rewrites `session`, and rendering
   * that means the whole transcript again — markdown, step logs and all — on the
   * main thread, behind a tab nobody is looking at, next to a preview iframe
   * that then stutters with it. Returning null makes an arriving step cost the
   * state write and nothing else; coming back renders once, from state that was
   * never lost.
   *
   * After every hook, never before one: an early return above them changes the
   * hook count between renders and React tears the card down — which would throw
   * away the very things this is here to keep.
   */
  if (!isActive) return null;

  return (
    <div className={classes.card}>
      {/* ── Session status ───────────────────────────────────────────────
          Always rendered, even before the first message: a card you opened by
          mistake needs a way out, and the ✕ lives here. */}
      <div className={classes.cardHeader}>
        <div className={classes.headerTop}>
          {session ? (
            <>
              <span className={classes.statusDot} style={{ backgroundColor: STATUS_COLORS[queue ? "queued" : status] || "#8f8f8f" }} />
              <span className={classes.statusLabel} style={{ color: STATUS_COLORS[queue ? "queued" : status] || "#8f8f8f" }}>
                {status === "running" && transportUncertain ? "Last known: running" : queue ? `queued · #${queue.position}` : status}
              </span>
              {session.startedBy && <span className={classes.owner}>{session.startedBy}</span>}
              <span className={classes.meta}>
                {turns.length} turn{turns.length === 1 ? "" : "s"}
                {isAdmin && turns.length ? (cost != null ? ` · ${cost.toFixed(3)}` : " · —") : ""}
                {session.lastMessageAt ? ` · ${formatWhen(session.lastMessageAt)}` : ""}
              </span>
              {session.sessionId || sessionIdRef.current ? (
                <Tooltip title={<span className={classes.sessionIdTip}>{session.sessionId || sessionIdRef.current}</span>}>
                  <InfoOutlinedIcon className={classes.sessionIdIcon} />
                </Tooltip>
              ) : null}
            </>
          ) : (
            <span className={classes.eyebrow}>{isConvert ? "New convert session" : "New session"}</span>
          )}

          {/* The socket, not the session: a card that says "running" while its
            view of the run is down is exactly the state that needs saying. */}
          {connectionState && connectionState !== "online" ? (
            <span
              className={classes.meta}
              style={{ color: connectionState === "offline" ? "#ef9a9a" : "#e0b96a" }}
            >
            · {connectionState === "offline" ? "disconnected" : `reconnecting…${connectionDetail?.attempt ? ` ${connectionDetail.attempt}/${connectionDetail.maxAttempts}` : ""}`}
            </span>
          ) : null}

          {/* The actions, in a group of their own: whatever the name and the
            counts come to, Stop / End session / ✕ stay on the row. They used
            to be plain children of it, so a long name pushed them out of
            sight. */}
          <div className={classes.headerActions}>
            {(connectionState === "offline" || status === "unreachable" || session?.transport?.exhausted) && !isClosed ? (
              <Button variant="outlined" size="small" className={classes.outlinedButton} onClick={reconnect}>
            Reconnect
              </Button>
            ) : null}
            {session && queue ? (
              <Tooltip title={`Start this turn without waiting, even though ${queue.running}/${queue.limit} agents are running.`}>
                <Button variant="outlined" size="small" className={classes.outlinedButton} onClick={startNow}>
              Start now
                </Button>
              </Tooltip>
            ) : null}
            {session && hasActiveOperation ? (
              <Button variant="outlined" size="small" className={classes.outlinedButton} onClick={stopTurn}>
                {session?.transport?.state === "stopping" ? "Stopping…" : "Stop"}
              </Button>
            ) : null}
            {session && !isClosed && !hasActiveOperation ? (
              <Tooltip title="Delete this session without merging">
                <span>
                  <IconButton
                    size="small"
                    className={classes.deleteButton}
                    onClick={() => setIsDeleteOpen(true)}
                    disabled={isEnding}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {session && !isClosed && !hasActiveOperation && session.workspaceReady !== false ? (
              <Tooltip title="Merge this session's work into master and close it">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.outlinedButton}
                    onClick={endSession}
                    disabled={isEnding}
                  >
                    {isEnding ? "Ending…" : "End session"}
                  </Button>
                </span>
              </Tooltip>
            ) : null}

            {canClose ? (
              <Tooltip
                title={
                  !session || isClosed
                    ? "Close this card"
                    : "Close this card (the session keeps running)"
                }
              >
                <IconButton
                  size="small"
                  onClick={closeCard}
                  disabled={!hasStarted && (busy || attachments.some((attachment) => ["queued", "uploading"].includes(attachment.status)))}
                  style={{ color: FIELD_LABEL }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        </div>

        {/* The name gets the line under the status, not a slot inside it: a
            row already carrying the owner, the counts and three actions left
            it a few characters wide, and the name is the one thing here worth
            reading whole. */}
        {session ? (
          <SessionTitle
            baseId={baseId}
            session={session}
            onRenamed={(title) => {
              // The card's own copy first: the list is told too, but the
              // header should not wait for a socket update to show the name
              // the user just typed.
              setSession((current) => (current ? { ...current, title: title || null } : current));
              if (onSessionRenamed) onSessionRenamed(session.sessionId, title);
            }}
          />
        ) : null}
      </div>

      {recovery ? <div className={classes.silentNote} role="status">{recoveryLabel(recovery, session?.provider || "Agent")}</div> : null}
      {status === "idle" && turns[turns.length - 1]?.status === "failed" && !isMissing ? (
        <Button variant="outlined" size="small" className={classes.outlinedButton} onClick={retryTurn} disabled={isSending || connectionState !== "online"}>
          Retry last request
        </Button>
      ) : null}
      {/* ── Composer ─────────────────────────────────────────────────── */}
      <div
        className={classes.composer}
        onDragOver={(event) => {
          const types = event.dataTransfer?.types;
          // A drag carrying files is a file drag, whatever the tree last
          // remembered: the remembered name is only ever a fallback for a
          // snapshot drag whose own dataTransfer read came up empty.
          const files = types?.includes("Files");
          const snapshot = types?.includes(SNAPSHOT_DRAG_TYPE) || (!files && Boolean(draggedSnapshot()));
          if (!snapshot && !files) return;

          // preventDefault is what lets the drop happen at all: without it the
          // browser refuses it and onDrop never runs.
          event.preventDefault();

          // A snapshot comes from the tree's react-dnd drag, and react-dnd
          // watches dragover on the window: over anything that is not one of
          // ITS drop targets it sets dropEffect to "none", and the browser
          // then cancels the drop without firing one. Stopping the event here
          // keeps that handler off this element — react-dnd has no target in
          // the composer to bookkeep anyway. A file drag is a native one,
          // which react-dnd leaves alone, so it travels on as before.
          if (snapshot) {
            event.stopPropagation();
            if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
          }
          if (!busy && !isClosed) setIsDropTarget(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setIsDropTarget(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDropTarget(false);
          // A snapshot dragged out of the library writes its name into the
          // prompt; it is not uploaded, the agent already has the file.
          // Files settle it; see the dragover handler above.
          const snapshot = event.dataTransfer.getData(SNAPSHOT_DRAG_TYPE)
            || (event.dataTransfer.files?.length ? null : draggedSnapshot());
          setDraggedSnapshot(null);
          // As in dragover: react-dnd would take this for a drop of its own.
          if (snapshot) event.stopPropagation();
          if (busy || isClosed) return;
          if (snapshot) {
            setDraft((current) => appendToPrompt(current, snapshot));
            return;
          }
          void handleFiles(event.dataTransfer.files);
        }}
        style={isDropTarget ? { outline: "2px dashed #64b5f6", outlineOffset: -2, background: "rgba(100,181,246,0.06)" } : undefined}
      >
        {isClosed ? (
          <Typography
            className={status === "failed" || isMissing ? classes.error : classes.muted}
          >
            {/* The full sentence for "failed" — including the id to quote — is
                on session.error, rendered right below this. */}
            {isMissing
              ? "This session no longer exists in the agent container and cannot be continued."
              : status === "failed"
                ? "This session could not be saved and has been closed; nothing is lost."
                : status === "expired"
                  ? "This session expired after sitting idle; its work is kept on its branch."
                  : session && session.discarded
                    ? "This session was deleted without merging. Snapshots it produced are unaffected."
                    : "This session is closed — its work has been merged into master."}
          </Typography>
        ) : (
          <>
            {isConvertStart ? (
              <ConvertSessionFields baseID={baseId} disabled={busy} value={convert} onChange={setConvert} />
            ) : null}
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                void handleFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <Button
                size="small"
                startIcon={<AttachFileIcon />}
                className={classes.outlinedButton}
                disabled={busy}
                onClick={() => attachmentInputRef.current?.click()}
              >
                Attach files
              </Button>
              <Typography className={classes.muted}>
                or drag and drop · any file type · up to 10 MB per file
              </Typography>
            </div>
            {attachments.map((attachment) => (
              <div key={attachment.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <Chip
                  size="small"
                  label={`${attachment.reference} ${attachment.file.name}`}
                  style={{ maxWidth: "70%", background: CONTROL_BG, color: "#ddd", border: `1px solid ${CONTROL_BORDER}` }}
                />
                {["queued", "uploading", "deleting"].includes(attachment.status) ? (
                  <>
                    <CircularProgress size={13} />
                    <Typography className={classes.muted}>
                      {attachment.status === "queued"
                        ? "Waiting to upload…"
                        : attachment.status === "deleting" ? "Removing…" : "Uploading…"}
                    </Typography>
                  </>
                ) : attachment.status === "deleteError" ? (
                  <Typography className={classes.error} role="alert">{attachment.error}</Typography>
                ) : attachment.status === "error" ? (
                  <>
                    <Typography className={classes.error} role="alert">{attachment.error}</Typography>
                    {attachment.file.size <= MAX_AGENT_ATTACHMENT_BYTES ? (
                      <Button size="small" disabled={busy} onClick={() => uploadAttachment(attachment)}>Retry</Button>
                    ) : null}
                  </>
                ) : (
                  <Typography style={{ color: "#a5d6a7", fontSize: 11 }}>Uploaded</Typography>
                )}
                <IconButton
                  size="small"
                  aria-label={`Remove ${attachment.file.name}`}
                  disabled={["queued", "uploading", "deleting"].includes(attachment.status)}
                  onClick={() => removeAttachment(attachment)}
                >
                  <CloseIcon style={{ fontSize: 15 }} />
                </IconButton>
              </div>
            ))}
            <div
              ref={promptComposerRef}
              style={{ position: "relative", width: "100%", backgroundColor: CONTROL_BG, borderRadius: 4 }}
            >
              <div
                ref={promptHighlightRef}
                aria-hidden="true"
                style={{
                  ...PROMPT_TEXT_METRICS,
                  position: "absolute",
                  zIndex: 0,
                  left: 0,
                  top: 0,
                  width: 0,
                  height: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                  color: "#fff",
                  boxSizing: "border-box",
                }}
              >
                <div ref={promptHighlightContentRef}>
                  {draft
                    ? highlightedPromptNodes(draft)
                    : <span style={{ color: "#999" }}>{hasStarted ? "Reply, or ask for a change…" : isConvertStart ? "Extra instruction for the port (optional)…" : "Describe what this session should produce…"}</span>}
                </div>
              </div>
              <TextField
                multiline
                minRows={3}
                maxRows={14}
                fullWidth
                variant="outlined"
                value={draft}
                onChange={handlePromptChange}
                disabled={busy}
                inputRef={promptInputRef}
                InputProps={{
                  style: {
                    position: "relative",
                    zIndex: 1,
                    padding: "18px 14px",
                    backgroundColor: "transparent",
                    fontSize: "13px",
                  },
                }}
                inputProps={{
                  style: {
                    ...PROMPT_TEXT_METRICS,
                    padding: 0,
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                    caretColor: "#fff",
                    backgroundColor: "transparent",
                  },
                  onKeyDown: handlePromptKeyDown,
                  onPaste: handlePromptPaste,
                  // These handlers must live on the native textarea. TextField
                  // itself is a wrapper and has no selectionStart/selectionEnd.
                  onClick: protectPromptCaret,
                  onMouseUp: protectPromptCaret,
                  onSelect: protectPromptCaret,
                  onScroll: (event) => {
                    if (promptHighlightContentRef.current) {
                      promptHighlightContentRef.current.style.transform =
                        `translate(${-event.currentTarget.scrollLeft}px, ${-event.currentTarget.scrollTop}px)`;
                    }
                  },
                }}
              />
            </div>

            <div className={classes.composerRow}>
              {/* Named before it starts, not after: a base runs a dozen of
                  these and the one you just opened is the one you can still
                  describe. Optional — an unnamed session reads as it always
                  did, and can be named from its header later. */}
              {!session && (
                <div style={{ minWidth: "200px" }}>
                  <div style={{ color: FIELD_LABEL, fontSize: "11px", lineHeight: "1.4" }}>
                    Session name
                  </div>
                  <input
                    className={classes.startNameInput}
                    value={startTitle}
                    maxLength={80}
                    placeholder="Optional"
                    onChange={(event) => setStartTitle(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </div>
              )}

              {/* The flow is NOT a choice: it belongs to the base being edited,
                  and the server reads it from the base document. Shown here so
                  it is obvious which pipeline — and which repository — a session
                  is working in. A running session keeps reporting its own flow,
                  which is what it was actually started with. */}
              <div style={{ minWidth: "148px" }}>
                <div style={{ color: FIELD_LABEL, fontSize: "11px", lineHeight: "1.4" }}>
                  Used flow
                </div>
                <div
                  title={usedFlowDescription}
                  style={{
                    color: usedFlow ? "#fff" : "#888",
                    fontSize: "13px",
                    padding: "5px 0",
                    fontFamily: "monospace",
                  }}
                >
                  {usedFlow || <em>unknown</em>}
                </div>
              </div>

              {isConvert ? (
                <div style={{ minWidth: "148px" }}>
                  <div style={{ color: FIELD_LABEL, fontSize: "11px", lineHeight: "1.4" }}>
                    Source
                  </div>
                  <div
                    title={_.get(convert, "game.path", "")}
                    style={{
                      color: convert.game ? "#fff" : "#888",
                      fontSize: "13px",
                      padding: "5px 0",
                      fontFamily: "monospace",
                    }}
                  >
                    {convert.game
                      ? `${convert.game.name}${convert.branch ? ` @ ${convert.branch}` : ""}`
                      : <em>no game picked</em>}
                  </div>
                </div>
              ) : null}

              {/* Model belongs to the conversation, so it locks once it starts. */}

              <FormControl size="small" style={{ minWidth: "138px" }}>
                <InputLabel shrink style={{ color: FIELD_LABEL }}>
                  Model
                </InputLabel>
                <Select
                  displayEmpty
                  value={model}
                  onChange={(e) => {
                    const next = e.target.value;
                    setModel(next);
                    // Levels differ per model; drop one the new model does not take.
                    const nextModel = (models || []).find((m) => m.id === (next || defaultModel));
                    if (effort && !_.includes(_.get(nextModel, "efforts"), effort)) setEffort("");
                  }}
                  disabled={busy || hasStarted}
                  style={{ color: "#fff", fontSize: "13px" }}
                  renderValue={(val) => (modelOptions.find((m) => m.value === val) || { label: val || "Flow default" }).label}
                >
                  {modelOptions.map((m) => (
                    <MenuItem key={m.value || "default"} value={m.value} disabled={m.disabled}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Effort also belongs to the conversation: set at start, then locked. */}
              <FormControl size="small" style={{ minWidth: "110px" }}>
                <InputLabel shrink style={{ color: FIELD_LABEL }}>
                  Effort
                </InputLabel>
                <Select
                  displayEmpty
                  value={effort}
                  onChange={(e) => setEffort(e.target.value)}
                  disabled={busy || hasStarted || !supportsEffort}
                  style={{ color: "#fff", fontSize: "13px" }}
                  renderValue={(val) => (effortOptions.find((o) => o.value === val) || { label: val }).label}
                >
                  {effortOptions.map((o) => (
                    <MenuItem key={o.value || "default"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div style={{ flex: 1 }} />

              <ConvertConfirmDialog
                open={isConfirmingConvert}
                game={convert.game}
                branch={convert.branch}
                detail={convert.detail}
                instructions={draft}
                onBack={() => setIsConfirmingConvert(false)}
                onConfirm={() => {
                  setIsConfirmingConvert(false);
                  submit(convertPrompt({ game: convert.game, branch: convert.branch, instructions: draft }));
                }}
              />

              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ flexShrink: 0, marginLeft: "auto" }}
                disabled={(isConvertStart ? !convert.game || !convert.branch : !draft.trim()) || busy || attachments.some((attachment) => attachment.status !== "uploaded") || available === null || (!hasStarted && !(selectedModel?.available || selectedModel?.retryable))}
                onClick={() => (isConvertStart ? setIsConfirmingConvert(true) : submit())}
              >
                {isSending ? "Sending…" : hasStarted ? "Send" : isConvertStart ? "Convert" : "Start"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* ── Transcript ───────────────────────────────────────────────── */}
      {turns.length ? (
        <div className={classes.body}>
          <div className={classes.turnTools}>
            {/* What the next turn will pay for in full, and the one thing that
                reduces it. Admin-only, like the other numbers on this card. */}
            {isAdmin && (uncachedTokens(session) || !supportsCompact) ? (
              <span className={classes.contextNote}>
                {uncachedTokens(session) ? `${formatTokens(uncachedTokens(session))} uncached` : "Context"}
                {!isClosed && !busy ? (
                  <>
                    {" · "}
                    <Tooltip
                      title={
                        !supportsCompact
                          ? "Codex compacts context automatically. Manual compaction is unavailable."
                          : armedCompact
                            ? "Press again to send /compact"
                            : "Ask the agent to compact its context. Runs as a turn — it takes a while and it costs."
                      }
                    >
                      <span
                        className={armedCompact ? classes.compactArmed : classes.compactLink}
                        onClick={supportsCompact ? compactSession : undefined}
                        aria-disabled={!supportsCompact}
                        style={!supportsCompact ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                      >
                        {armedCompact ? "press again to compact" : "compact?"}
                      </span>
                    </Tooltip>
                  </>
                ) : null}
              </span>
            ) : null}
            <div style={{ flex: 1 }} />
            <Tooltip title="Expand all turns">
              <IconButton
                size="small"
                className={classes.turnToolButton}
                onClick={() => setAllTurns(true)}
              >
                <UnfoldMoreIcon style={{ fontSize: "15px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all turns">
              <IconButton
                size="small"
                className={classes.turnToolButton}
                onClick={() => setAllTurns(false)}
              >
                <UnfoldLessIcon style={{ fontSize: "15px" }} />
              </IconButton>
            </Tooltip>
          </div>
          {[...turns].reverse().map((turn, reverseIndex) => {
            const index = turns.length - 1 - reverseIndex;
            return (
              <Turn
                key={`${turn.startedAt}-${index}`}
                turn={turn}
                uncertain={Boolean(transportUncertain)}
                queue={index === turns.length - 1 ? queue : null}
                index={index}
                isAdmin={isAdmin}
                isOpen={isTurnOpen(index)}
                onToggle={() => setTurnOverrides((prev) => ({ ...prev, [index]: !isTurnOpen(index) }))}
                isLogOpen={isLogOpen(index, turn)}
                onToggleLog={() => setLogOverrides((prev) => ({ ...prev, [index]: !isLogOpen(index, turn) }))}
              />
            );
          })}
        </div>
      ) : null}

      {/* Shaped like the dashboard's other delete dialogs (DialogDeleteShell):
          same widths, same title size, same red/grey button pair — a destructive
          confirmation should not look like a new kind of thing.
          Not that shell itself, though: it is built around a media record, with
          usage lookups and a cascade it can cancel, none of which exist here.

          The wording names what goes AND what stays, because those are the easy
          two to confuse — snapshots and assets live in the flow's workspace
          through symlinks, so they were never inside what is being removed. */}
      <Dialog
        open={isDeleteOpen}
        fullWidth
        maxWidth="xs"
        onClose={() => setIsDeleteOpen(false)}
      >
        <DialogTitle style={{ fontSize: "1.4rem", padding: "16px 16px 8px" }}>
          Delete this session?
        </DialogTitle>
        <div style={{ margin: "0 16px 16px" }}>
          <Typography style={{ color: "#ddd", fontSize: "13px", marginBottom: "12px" }}>
            The <strong>workspace and the conversation</strong> will be permanently
            deleted. Nothing is merged and no branch is kept.
          </Typography>
          <Typography style={{ color: "#9bcec0", fontSize: "13px", marginBottom: "12px" }}>
            Snapshots and assets this session produced are not affected.
          </Typography>
          <div style={{ marginTop: "12px" }}>
            <Button
              variant="contained"
              style={{ backgroundColor: "#CC736E", color: "#fce4ec" }}
              onClick={discardSession}
            >
              DELETE
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "#555", color: "#eee", marginLeft: "8px" }}
              onClick={() => setIsDeleteOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>

      {session && session.error && !isRunning ? (
        <Typography className={classes.error} style={{ padding: "0 10px 10px" }}>
          {session.error}
        </Typography>
      ) : null}
    </div>
  );
}

SessionCard.propTypes = {
  baseId: PropTypes.string,
  /** False while the tab is hidden: the card stays alive but renders nothing. */
  isActive: PropTypes.bool,
  isAdmin: PropTypes.bool,
  available: PropTypes.bool,
  /** The flow this base drives — displayed, never chosen. */
  baseFlow: PropTypes.string,
  flows: PropTypes.array,
  models: PropTypes.array,
  initialSession: PropTypes.object,
  isConvert: PropTypes.bool,
  observedOperationId: PropTypes.string,
  /** Reports the conversation this card owns so the picker cannot offer it too. */
  onSessionOpened: PropTypes.func,
  onTurnFinished: PropTypes.func,
  /** Called with the session id once it can never be continued again. */
  onSessionClosed: PropTypes.func,
  onRemove: PropTypes.func,
  canRemove: PropTypes.bool,
  createSnackbar: PropTypes.func
};

export default function ContainerSnapshotAgent(props) {
  const classes = useStyles();
  const baseId = String(_.get(props, "selectedBase._id", ""));
  const baseFlow = snapshotFlowOf(props.selectedBase) || "";
  const isAdmin = props.isAdmin === true;
  // The tab keeps this panel mounted when you switch away from it (see
  // containerSnapshotConfig) and says so here. Defaults to true so anywhere that
  // renders the panel on its own gets the ordinary, visible behaviour.
  const isActive = props.isActive !== false;

  const [available, setAvailable] = useState(null); // null = still checking
  const [unavailableReason, setUnavailableReason] = useState("");
  const [flows, setFlows] = useState([]);
  const [models, setModels] = useState([]);
  const [resumable, setResumable] = useState([]);
  // Container-wide slot usage plus this flow's waiting turns, refreshed with discovery.
  const [agentQueue, setAgentQueue] = useState(null);

  const [slots, setSlots] = useState([{ key: "slot-0", initialSession: null }]);
  const slotSeq = useRef(1);
  const knownSessionIds = useRef(new Set());

  // A turn (in any card) finished: the snapshot library on the left may have new
  // files, so tell the editor to reload the tree, then say what happened.
  const handleTurnFinished = useCallback(
    (session, turn) => {
      props.onSnapshotsChanged?.();

      if (!props.createSnackbar || !turn) return;
      const count = (_.get(turn, "newSnapshots") || []).length;

      if (turn.status === "succeeded") {
        props.createSnackbar(`Agent replied — ${count} new snapshot${count === 1 ? "" : "s"}`, "success");
      } else {
        props.createSnackbar(turn.error || "Agent turn failed", "error");
      }
    },
    [props.createSnackbar, props.onSnapshotsChanged]
  );

  // A conversation that reached its end — merged, expired, failed to merge, or
  // gone from the container altogether. Dropping it here is what stops the ✕ on
  // its card from putting it back under "Continue a session": the picker is
  // built from this list, and the card was the only thing hiding it.
  const forgetSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setResumable((prev) => prev.filter((s) => s.sessionId !== sessionId));
  }, []);

  const rememberSession = useCallback((session) => {
    if (!session?.sessionId || ["ended", "expired", "failed"].includes(session.status)) return;
    setResumable((prev) => [
      session,
      ...prev.filter((item) => item.sessionId !== session.sessionId),
    ]);
  }, []);

  const addSessionCard = useCallback((initialSession) => {
    const key = `slot-${slotSeq.current++}`;
    setSlots((prev) => [...prev, { key, initialSession: initialSession || null }]);
  }, []);

  const addConvertCard = useCallback(() => {
    const key = `slot-${slotSeq.current++}`;
    setSlots((prev) => [...prev, { key, initialSession: null, isConvert: true }]);
  }, []);

  const removeSessionCard = useCallback((key) => {
    setSlots((prev) => {
      const next = prev.filter((s) => s.key !== key);
      // Closing the last card hands back an empty one rather than an empty tab:
      // the card is also where a new conversation is started, so leaving none
      // would strand anyone who did not spot "+ New session".
      return next.length
        ? next
        : [{ key: `slot-${slotSeq.current++}`, initialSession: null }];
    });
  }, []);

  // A card created empty learns its session id only when the first prompt is
  // sent. Keep that live id on the slot so discovery cannot offer the same
  // conversation under "Continue a session" while its card is already open.
  const markSessionOpen = useCallback((key, sessionId) => {
    if (!sessionId) return;
    setSlots((prev) => slotsWithSessionId(prev, key, sessionId));
  }, []);

  useEffect(() => {
    if (!baseId) return undefined;
    let cancelled = false;

    fetchSnapshotAgentFlows(baseId)
      .then((data) => {
        if (cancelled) return;
        setAvailable(data.available === true);
        setUnavailableReason(data.error || "");
        setFlows(data.flows || []);
        setModels(data.models || []);

        // "failed" is as finished as "ended": the container tore the session
        // down when its merge would not go through, so continuing it is not on
        // offer — listing it would only produce an attach that finds nothing.
        const sessions = (data.sessions || []).filter(
          (s) => s && s.status !== "ended" && s.status !== "expired" && s.status !== "failed"
        );
        knownSessionIds.current = new Set(sessions.map((s) => s.sessionId));
        setResumable(sessions);

        // Re-open whatever is still working as its own card, then always add one
        // empty card to start something new. Idle sessions stay in the picker
        // rather than opening on their own — there may be many.
        const working = sessions.filter((s) => s.status === "running");
        const attachSlots = working.map((s) => ({
          key: `slot-${slotSeq.current++}`,
          initialSession: s
        }));
        setSlots([...attachSlots, { key: `slot-${slotSeq.current++}`, initialSession: null }]);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to reach the agent pipeline:", err);
        setAvailable(false);
        setUnavailableReason(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [baseId]);

  useEffect(() => {
    if (!baseId || !isActive || available !== true) return undefined;
    let cancelled = false;
    let timer;

    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const data = await fetchSnapshotAgentSessions(baseId);
        if (cancelled) return;
        const sessions = (data.sessions || []).filter(
          (s) => s && !["ended", "expired", "failed"].includes(s.status)
        );
        // Idle with nothing in it is not a conversation anyone can open: the
        // container lists a session whose first turn ended before it had a
        // workspace, and a card for one can neither attach nor be continued.
        // It stays in the picker; it just does not open itself on every poll.
        // Not remembered either, so the real thing still opens if one arrives.
        const empty = (s) => !s.turnCount && s.status !== "running";
        const newlyDiscovered = sessions.filter((s) =>
          AUTOMATION_ACTORS.has(s.startedBy) &&
          !knownSessionIds.current.has(s.sessionId) &&
          !empty(s)
        );
        for (const session of sessions) if (!empty(session)) knownSessionIds.current.add(session.sessionId);
        setResumable(sessions);
        const nextQueue = data.queue || null;
        setAgentQueue((prev) => (JSON.stringify(prev) === JSON.stringify(nextQueue) ? prev : nextQueue));
        setSlots((prev) => {
          const openIds = new Set(prev.map((slot) => slot.initialSession?.sessionId).filter(Boolean));
          const byId = new Map(sessions.map((session) => [session.sessionId, session]));
          let changed = false;
          const updated = prev.map((slot) => {
            const latest = byId.get(slot.initialSession?.sessionId);
            if (!latest?.operationId || latest.operationId === (slot.observedOperationId || slot.initialSession?.operationId)) return slot;
            changed = true;
            return { ...slot, observedOperationId: latest.operationId };
          });
          for (const session of newlyDiscovered) {
            if (openIds.has(session.sessionId)) continue;
            changed = true;
            updated.push({ key: `slot-${slotSeq.current++}`, initialSession: session,
              observedOperationId: session.operationId });
          }
          return changed ? updated : prev;
        });
      } catch (err) {
        // Existing cards keep their sockets and state during a brief outage.
        console.warn("[snapshotAgent] session discovery failed:", err.message);
      }
    };
    const schedule = () => {
      timer = setTimeout(async () => {
        await refresh();
        if (!cancelled) schedule();
      }, SESSION_DISCOVERY_MS);
    };
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    void refresh();
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [baseId, isActive, available]);

  // Running sessions fetched on page load already have an open card and are
  // excluded here. If the user deliberately closes that card, keep the
  // still-running conversation available here so it can be reopened.
  // A rename lands in the lists at once rather than on the next poll.
  const handleSessionRenamed = useCallback((sessionId, title) => {
    const withTitle = (session) =>
      session && session.sessionId === sessionId ? { ...session, title } : session;
    setResumable((prev) => prev.map(withTitle));
    setSlots((prev) => prev.map((slot) => ({ ...slot, initialSession: withTitle(slot.initialSession) })));
  }, []);

  // Every name already in use on this base, so an automatic one can avoid them.
  const takenTitles = useMemo(
    () => _.uniq([
      ...resumable.map((item) => item.title),
      ...slots.map((slot) => _.get(slot, "initialSession.title")),
    ].filter(Boolean)),
    [resumable, slots]
  );

  const pickable = pickableAgentSessions(resumable, slots);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <span className={classes.eyebrow}>Agent Pipeline</span>
        {available !== false ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              size="small"
              variant="outlined"
              className={classes.outlinedButton}
              onClick={() => addSessionCard(null)}
              disabled={available === null}
            >
              + New session
            </Button>
            {baseFlow === CONVERT_FLOW ? (
              <Button
                size="small"
                variant="outlined"
                className={classes.outlinedButton}
                onClick={() => addConvertCard()}
                disabled={available === null}
              >
                + New convert session
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {available === false ? (
        <Typography className={classes.muted}>
          The agent container is unreachable, so sessions cannot be started right now.
          {isAdmin && unavailableReason ? ` (${unavailableReason})` : ""}
        </Typography>
      ) : (
        <>
          <AgentQueuePanel queue={agentQueue} />

          {/* Conversations waiting for a reply — including ones from earlier days,
              which the container replays when opened. */}
          {pickable.length ? (
            <div>
              <div className={classes.eyebrow} style={{ marginBottom: "6px" }}>
                Continue a session
              </div>
              <div className={classes.picker}>
                {pickable.map((s) => {
                  const count = s.turns && s.turns.length ? s.turns.length : s.turnCount || 0;
                  return (
                    <Button key={s.sessionId} className={classes.pickerCard} onClick={() => addSessionCard(s)}>
                      {/* One column of its own: a Button lays its children out
                          in a row, which ran the name straight into the line
                          under it — "01:31 PMaynur". */}
                      <span className={classes.pickerLines}>
                        <span className={classes.pickerWhen}>{s.title || formatWhen(s.lastMessageAt)}</span>
                        <span className={classes.pickerMeta}>
                          <span className={classes.pickerOwner}>{s.startedBy || "unknown"}</span>
                          {` · ${count} turn${count === 1 ? "" : "s"}`}
                          {s.title ? ` · ${formatWhen(s.lastMessageAt)}` : ""}
                        </span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {slots.map((slot) => (
              <SessionCard
                key={slot.key}
                baseId={baseId}
                onSessionRenamed={handleSessionRenamed}
                takenTitles={takenTitles}
                isActive={isActive}
                isAdmin={isAdmin}
                available={available}
                baseFlow={baseFlow}
                flows={flows}
                models={models}
                initialSession={slot.initialSession}
                isConvert={slot.isConvert === true}
                observedOperationId={slot.observedOperationId || slot.initialSession?.operationId}
                onSessionOpened={(sessionId) => markSessionOpen(slot.key, sessionId)}
                onTurnFinished={handleTurnFinished}
                onSessionClosed={forgetSession}
                onRemove={(session) => {
                  rememberSession(session);
                  removeSessionCard(slot.key);
                }}
                canRemove={slots.length > 1}
                createSnackbar={props.createSnackbar}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * How many agents run, how many wait, and in what order. The order is the
 * container's: arrival order. Only this flow's turns are
 * listed; the others are counted, since they belong to a base the viewer is not on.
 */
function AgentQueuePanel({ queue }) {
  const classes = useStyles();
  if (!queue || (!queue.queued && queue.running < queue.limit)) return null;
  return (
    <div>
      <div className={classes.eyebrow} style={{ marginBottom: "2px" }}>Agent queue</div>
      <span className={classes.meta}>
        {queue.running}/{queue.limit} agents running · {queue.queued} waiting
        {queue.otherFlows ? ` · ${queue.otherFlows} from other bases` : ""}
      </span>
      {queue.items.length ? (
        <div className={classes.queueList}>
          {queue.items.map((item) => (
            <div key={item.operationId || item.sessionId} className={classes.queueRow}>
              <span className={classes.queuePosition}>#{item.position}</span>
              <span>{item.kind === "start" ? "New session" : "Follow-up"}</span>
              <span className={classes.meta}>
                {item.promptBy || "unknown"}
                {item.queuedAt ? ` · waiting since ${formatWhen(item.queuedAt)}` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

AgentQueuePanel.propTypes = {
  queue: PropTypes.object
};

ContainerSnapshotAgent.propTypes = {
  selectedBase: PropTypes.object,
  /** False while the tab is hidden — the panel stays mounted, see the note above. */
  isActive: PropTypes.bool,
  isAdmin: PropTypes.bool,
  createSnackbar: PropTypes.func,
  /** Called when a turn finishes, so the snapshot tree can pick up new files. */
  onSnapshotsChanged: PropTypes.func
};
